import { IRequestCancellationManager } from './interfaces/IRequestCancellationManager';
import logger from './loggerService';

/**
 * Enterprise-grade, key-scoped request cancellation manager.
 * Tracks active HTTP requests using AbortControllers and automatically
 * aborts superseded operations to prevent race conditions and unnecessary load.
 */
export class RequestCancellationManager implements IRequestCancellationManager {
  private static instance: RequestCancellationManager;
  private readonly controllers = new Map<string, AbortController>();

  private constructor() {}

  /**
   * Singleton accessor for global request cancellation.
   */
  public static getInstance(): RequestCancellationManager {
    if (!RequestCancellationManager.instance) {
      RequestCancellationManager.instance = new RequestCancellationManager();
    }
    return RequestCancellationManager.instance;
  }

  /**
   * Registers a new request under a specific key.
   * If a previous request is already active for this key, it is aborted
   * immediately before starting the new request.
   */
  public register(key: string): AbortSignal {
    if (this.controllers.has(key)) {
      logger.debug?.(`Superseding request detected. Aborting previous request for key: ${key}`, { key });
      this.cancel(key, 'superseded');
    }

    const controller = new AbortController();
    this.controllers.set(key, controller);
    return controller.signal;
  }

  /**
   * Manually cancels/aborts a pending request associated with a key.
   */
  public cancel(key: string, reason?: string): void {
    const controller = this.controllers.get(key);
    if (controller) {
      try {
        controller.abort(reason || 'user_cancelled');
        logger.debug?.(`Successfully aborted request for key: ${key}. Reason: ${reason || 'user_cancelled'}`, { key, reason });
      } catch (error) {
        logger.error?.(`Error while aborting request for key: ${key}`, error, 'RequestCancellationManager');
      } finally {
        this.controllers.delete(key);
      }
    }
  }

  /**
   * Cancels all pending requests globally.
   * Used during logouts, session expiration, or full application teardown.
   */
  public cancelAll(reason?: string): void {
    logger.info?.(`Initiating global request cancellation. Reason: ${reason || 'global_cleanup'}`, { reason });
    const keys = Array.from(this.controllers.keys());
    for (const key of keys) {
      this.cancel(key, reason || 'global_cleanup');
    }
    this.controllers.clear();
  }

  /**
   * Checks if there is an active request tracked under the given key.
   */
  public isActive(key: string): boolean {
    const controller = this.controllers.get(key);
    return !!controller && !controller.signal.aborted;
  }

  /**
   * Removes tracking for a completed request.
   * Prevents memory leaks by ensuring finished requests are garbage collected.
   * Verifies the target AbortSignal is the current active one before deleting.
   */
  public complete(key: string, signal?: AbortSignal): void {
    const controller = this.controllers.get(key);
    if (controller && (!signal || controller.signal === signal)) {
      this.controllers.delete(key);
    }
  }
}

export default RequestCancellationManager.getInstance();
